import React, {ReactNode, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../../theme/rewardsTheme';

import SignUpPasswordScreenContent from './SignUpPasswordScreenContent';

/**
 * Sign-up Screen 2, reached when the address entered on Screen 1 belongs to an
 * existing account. The frame selects the layout, exactly as on Screen 1.
 *
 * Every story renders through its own `render` and takes no story args, because
 * the in-app browser at `app/screens/StorybookScreen.web.tsx` calls
 * `story.render()` with no arguments and `decorator(Story)` with no context.
 */
const MobileFrame = ({children}: {children: ReactNode}) => (
  <View style={styles.mobileFrame}>{children}</View>
);

const DesktopFrame = ({children}: {children: ReactNode}) => (
  <View style={styles.desktopFrame}>{children}</View>
);

const EMAIL = 'johnsmith@gmail.com';

const noop = () => {};
const logLogIn = () => console.log('Log in pressed');
const logBack = () => console.log('Back pressed');
const logForgot = () => console.log('Forgot Password pressed');

const meta = {
  title: 'Screens/Sign Up/Password',
  component: SignUpPasswordScreenContent,
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <Story />
      </ThemeProvider>
    ),
  ],
  parameters: {
    layout: 'fullscreen',
    docs: {
      description: {
        component:
          'Sign-up Screen 2: password entry for an existing account. Presentational only — no navigation, no store, no API.\n\n' +
          '**Design annotations applied:**\n' +
          '- The brand header in the Figma frame is NOT rendered; only the back arrow remains, which returns to the email step.\n' +
          '- Heading and body copy are centred; the field labels stay left-aligned.\n' +
          '- The email field carries the address from Screen 1 in the FormField disabled/prefilled state.\n' +
          '- The password field uses the FormField visibility toggle.\n' +
          '- The link reads "Forgot Password?".\n' +
          '- LOG IN is full width and stays disabled until `loginSchema` accepts both fields, so a password under 8 characters keeps it disabled.',
      },
    },
  },
  args: {
    email: EMAIL,
    password: '',
    onChangePassword: noop,
    onLogIn: logLogIn,
    onBack: logBack,
    onForgotPassword: logForgot,
  },
} satisfies Meta<typeof SignUpPasswordScreenContent>;

export default meta;

type Story = StoryObj<typeof meta>;

/** Empty password on the mobile layout: the LOG IN button is disabled. */
export const MobileCtaDisabled: Story = {
  name: 'Mobile — CTA disabled (empty password)',
  render: () => (
    <MobileFrame>
      <SignUpPasswordScreenContent
        email={EMAIL}
        password=""
        onChangePassword={noop}
        onLogIn={logLogIn}
        onBack={logBack}
        onForgotPassword={logForgot}
      />
    </MobileFrame>
  ),
};

/** Seven characters fail `passwordSchema`'s 8-character minimum. */
export const MobileCtaDisabledShort: Story = {
  name: 'Mobile — CTA disabled (password too short)',
  render: () => (
    <MobileFrame>
      <SignUpPasswordScreenContent
        email={EMAIL}
        password="short12"
        onChangePassword={noop}
        onLogIn={logLogIn}
        onBack={logBack}
        onForgotPassword={logForgot}
      />
    </MobileFrame>
  ),
};

/** Both fields valid: the LOG IN button is enabled and uses the brand primary. */
export const MobileCtaEnabled: Story = {
  name: 'Mobile — CTA enabled (valid password)',
  render: () => (
    <MobileFrame>
      <SignUpPasswordScreenContent
        email={EMAIL}
        password="hunter2hunter2"
        onChangePassword={noop}
        onLogIn={logLogIn}
        onBack={logBack}
        onForgotPassword={logForgot}
      />
    </MobileFrame>
  ),
};

/** A rejected password: the field takes the FormField error chrome. */
export const MobileError: Story = {
  name: 'Mobile — Error (password rejected)',
  render: () => (
    <MobileFrame>
      <SignUpPasswordScreenContent
        email={EMAIL}
        password="wrongpassword"
        onChangePassword={noop}
        onLogIn={logLogIn}
        onBack={logBack}
        onForgotPassword={logForgot}
        errorMessage="That password doesn't match this account."
      />
    </MobileFrame>
  ),
};

/** Typing in the password field toggles the CTA between its two states. */
export const MobileInteractive: Story = {
  name: 'Mobile — Interactive (type to enable)',
  render: () => {
    const [password, setPassword] = useState('');

    return (
      <MobileFrame>
        <SignUpPasswordScreenContent
          email={EMAIL}
          password={password}
          onChangePassword={setPassword}
          onLogIn={logLogIn}
          onBack={logBack}
          onForgotPassword={logForgot}
        />
      </MobileFrame>
    );
  },
};

/** Empty password on the desktop layout: the column stays at reading width. */
export const DesktopCtaDisabled: Story = {
  name: 'Desktop — CTA disabled (empty password)',
  render: () => (
    <DesktopFrame>
      <SignUpPasswordScreenContent
        email={EMAIL}
        password=""
        onChangePassword={noop}
        onLogIn={logLogIn}
        onBack={logBack}
        onForgotPassword={logForgot}
      />
    </DesktopFrame>
  ),
};

/** Typing in the field toggles the CTA on the desktop layout. */
export const DesktopInteractive: Story = {
  name: 'Desktop — Interactive (type to enable)',
  render: () => {
    const [password, setPassword] = useState('');

    return (
      <DesktopFrame>
        <SignUpPasswordScreenContent
          email={EMAIL}
          password={password}
          onChangePassword={setPassword}
          onLogIn={logLogIn}
          onBack={logBack}
          onForgotPassword={logForgot}
        />
      </DesktopFrame>
    );
  },
};

const styles = StyleSheet.create({
  mobileFrame: {
    width: 375,
    height: 812,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
  },
  desktopFrame: {
    width: '100%',
    minHeight: 700,
    backgroundColor: '#ffffff',
  },
});
